import React, { useEffect, useState } from 'react';
import { ButtonGroup } from '@atlaskit/button';
import AtlaskitButton from '@atlaskit/button/loading-button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { view } from '@forge/bridge';
import { router } from '@forge/bridge';
import { FullContext } from '@forge/bridge/out/types';
import { uniqBy } from 'lodash';

import { ImportExportLevel } from '../../../../../src/services/import-export/public_model';
import { OptionItemObject } from '../../../../../src/shared_models/interfaces';
import licenseApi from '../../api/licenseApi';
import storageApi from '../../api/storageApi';
import userApi from '../../api/userApi';
import { ErrorResponseObject, LangCodes, LevelsInfo } from '../../interfaces';
import {
    ADD_OPTION_CLICKED,
    CANCEL_SUCCESS_MESSAGE,
    CANCEL_TRIGGERED,
    DATA_FOR_VIEW_LOADED,
    DEFAULT_VALUE_CHANGED,
    FIRST_NESTING_LEVEL,
    GET_FIELD_CONFIG_FAILED_MESSAGE,
    IMPORT_SUCCESS_MESSAGE,
    IS_ENABLED,
    IS_TRANSLATION_UPDATE,
    KEY,
    LEVEL_DESCRIPTION_KEY,
    LEVEL_DESCRIPTION_TRANSLATION_KEY,
    LEVEL_NAME_KEY,
    LEVEL_NAME_TRANSLATION_KEY,
    LEVELS_INFO_DEFAULTS,
    MAX_NESTING_LEVEL,
    OPTION_TO_DELETE_DEFAULTS,
    REORDER_OPTIONS,
    SAVE_OPTIONS_FAILED_MESSAGE,
    SAVE_TO_STORAGE,
    SETTINGS_SAVED_FAILURE_MESSAGE,
    SETTINGS_SAVED_SUCCESS_MESSAGE,
    SORT_OPTIONS,
    STRAIGHT_UPDATE,
    TOGGLE_STATE_CHANGED,
    TOP_OF_THE_TREE,
    TRANSLATION_VALUE_CHANGED,
    TREE_DATA_WITH_LEVELS_INFO_CONFIG_MODEL,
    UNDEFINED,
    VALUE_CHANGED,
    VALUE_UPDATE,
} from '../../utils/constants';
import { mergeWithCurrentOptions } from '../../utils/importOptionsMapper';
import {
    findOptionByPropertyAndValue,
    getAllOptionsByDepthLevel,
    getDepthLevelRelatedOptions,
    getFieldTreeData,
    getLevelsInfoData,
    getOptionsWithUpdatedName,
    getOptionsWithUpdatedNameForImport,
    getParentNode,
    getParentsDataForCurrentNestingLevel,
    getSaveModelSuccessMessage,
    handleBulkOptions,
    logResponseError,
    removeOptionFromTree,
    updateDefaultOptionInCurrentLevel,
    updateLevelInfoFirstOptionKey,
    updateLevelOneOptions,
    updateLevelsInfoFirstOptionKey,
    updateLevelsInfoNameOrDescription,
    updateLevelsInfoNameOrDescriptionTranslation,
    updateOrderInLevelOneOptions,
    updatePropertyInOption,
    updateTreeOptionAndUpdateTreeDepths,
    updateTreeWithCurrentState,
} from '../../utils/levelModelUtils';
import { LicenseBanner } from '../../utils/LicenseBanner';
import Flag, { addErrorFlag, addSuccessFlag } from '../common/flag/Flag';
import { FlagItem } from '../common/flag/interfaces';

import { OptionsSortingData, OptionTranslationValue } from './options-adder/interfaces';
import OptionsAdder from './options-adder/OptionsAdder';
import { OptionAdderOnChangeTypes, OptionsAdderAllowedValueTypes } from './options-adder/types';
import {
    copyArrayWithoutReference,
    removeOption,
    sortOptionsByGivenDirection,
    updateOptionsAdder,
    updateSingleOption,
} from './options-adder/utils';
import PreviewConfigurationModal from './preview/PreviewConfigurationModal';
import AddBulkOptionsModal from './AddBulkOptionsModal';
import { ABSOLUTE } from './constants';
import { Wrapper } from './CustomFieldConfigStyled';
import DeleteOptionModal from './DeleteOptionModal';
import ImportConfigurationModal from './import-configuration';
import { OptionToDeleteData, OptionWithUpdatedName, ParentOptionData } from './interfaces';
import LastLevelInfoMessage from './LastLevelInfoMessage';
import LevelsInformation from './LevelsInformation';
import OptionDisabledWarning from './OptionDisabledWarning';
import SettingsModal from './SettingsModal';
import { BottomSection, FieldConfigContent, FullWidthLine, PositionedElement } from './styles';
import TopBar from './TopBar';
import UpdateOptionNameModal from './UpdateOptionNameModal';

export const isFirstBreadcrumbLevel = (currentBreadcrumb: number) => currentBreadcrumb === 1;

const CONTENT = 'content';


const CustomFieldConfig = () => {
    const [editedFieldId, setEditedFieldId] = useState('');
    const [editedContextId, setEditedContextId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [selectOptions, setSelectOptions] = useState<OptionItemObject[]>([]);
    const [options, setOptions] = useState<OptionItemObject[]>([]);
    const [currentParentOptionKey, setCurrentParentOptionKey] = useState('root');
    const [currentNestingLevel, setCurrentNestingLevel] = useState(1);
    const [isSavingTriggered, setIsSavingTriggered] = useState(false);
    const [flags, setFlags] = useState<FlagItem[]>([]);
    const [dataFetched, setDataFetched] = useState(false);
    const [disabledParentOption, setDisabledParentOption] = useState<OptionItemObject | undefined>();
    const [isAddBulkOptionsModalOpened, setIsAddBulkOptionsModalOpened] = useState(false);
    const [isDeleteOptionModalOpened, setIsDeleteOptionModalOpened] = useState(false);
    const [nestingLevelChanged, setNestingLevelChanged] = useState(false);
    const [currentLevelParents, setCurrentLevelParents] = useState<ParentOptionData[] | undefined>([]);
    const [optionToDelete, setOptionToDelete] = useState<OptionToDeleteData>({ ...OPTION_TO_DELETE_DEFAULTS });
    const [isOptionDeletionInProgress, setIsOptionDeletionInProgress] = useState(false);
    const [isOptionNameUpdated, setIsOptionNameUpdated] = useState(false);
    const [isUpdateOptionNameModalOpened, setIsUpdateOptionNameModalOpened] = useState(false);
    const [optionsWithUpdatedName, setOptionsWithUpdatedName] = useState<OptionWithUpdatedName[]>([]);
    const [levelOptionsInitialState, setLevelOptionsInitialState] = useState<OptionItemObject[]>([]);
    const [levelsInfo, setLevelsInfo] = useState<LevelsInfo>({ ...LEVELS_INFO_DEFAULTS });
    const [locale, setLocale] = useState<LangCodes>('');
    const [isPreviewConfigurationModalOpened, setIsPreviewConfigurationModalOpened] = useState(false);
    const [isSettingsModalOpened, setIsSettingsModalOpened] = useState(false);
    const [isImportConfigModalOpened, setIsImportConfigModalOpened] = useState(false);

    const fetchData = async (isCancelTriggered = false) => {
        try {
            const [isLicenseActive, context, localeValue]: [isActiveValue: boolean, context: any, localeValue: LangCodes] =
                await Promise.all([licenseApi.getIsLicenseActive(), view.getContext(), userApi.getLocale()]);
            const { fieldId } = context.extension;
            const contextId = getContextIdFromContext(context);

            setEditedFieldId(fieldId);
            setEditedContextId(contextId);
            setLocale(localeValue);

            await setTreeDataConfig(fieldId, contextId, isCancelTriggered);

            setIsActive(isLicenseActive);

            setDataFetched(true);
            setIsOptionNameUpdated(false);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const removeBodyVerticalScroll = () => {
        document.body.style.overflowY = 'hidden';
    };

    useEffect(() => {
        void fetchData();
        removeBodyVerticalScroll();
    }, []);

    const getContextIdFromContext = (context: FullContext) => {
        //MS-159
        const { configurationId } = context.extension;
        return configurationId.toString();
    };

    const isUpdateLevelWithNoOptions = (options: OptionItemObject[]) => options.length === 0;

    const getOptionsWithoutEmptyOnesAndDuplicates = (selectOptions: OptionItemObject[]) => {
        const values = selectOptions.map(option => option.value && option.value.trim());

        return selectOptions.filter(option => option.value).filter(({ value }, index) => !values.includes(value, index + 1));
    };

    const handleModelSave = () => {
        (document.activeElement as HTMLElement).blur();

        if (isOptionNameUpdated) {
            setIsUpdateOptionNameModalOpened(true);

            return;
        }

        const isUpdateWithNoOptions: boolean = isUpdateLevelWithNoOptions(selectOptions);
        const updatedOptions: OptionItemObject[] =
            isUpdateWithNoOptions ? [] : getOptionsWithoutEmptyOnesAndDuplicates(selectOptions);
        updateTreeWithCurrentState(
            options,
            currentParentOptionKey,
            currentNestingLevel,
            updatedOptions,
            setOptions,
            setIsSavingTriggered,
            SAVE_TO_STORAGE
        );
        setSelectOptions(updatedOptions);
    };

    const saveModel = async () => {
        setIsLoading(true);
        try {
            await storageApi.saveConfig(editedFieldId, editedContextId, {
                treeData: options,
                levelsInfo,
                version: TREE_DATA_WITH_LEVELS_INFO_CONFIG_MODEL,
            });
            setFlags(addSuccessFlag(flags, getSaveModelSuccessMessage(isOptionDeletionInProgress, isOptionNameUpdated)));
        } catch (error) {
            logResponseError(error as ErrorResponseObject);
            setFlags(addErrorFlag(flags, SAVE_OPTIONS_FAILED_MESSAGE));
        }
        setIsLoading(false);
        setIsSavingTriggered(false);
        isOptionDeletionInProgress && setIsOptionDeletionInProgress(false);
        isOptionNameUpdated && setIsOptionNameUpdated(false);
    };

    const setTreeDataConfig = async (fieldId: string, contextId: string, isCancelTriggered: boolean) => {
        try {
            const loadedData = await storageApi.getConfig(fieldId, contextId);
            const filteredTreeData = getFieldTreeData(loadedData);
            setOptions(filteredTreeData as OptionItemObject[]);
            setLevelsInfo(getLevelsInfoData(loadedData));
            isCancelTriggered && setFlags(addSuccessFlag(flags, CANCEL_SUCCESS_MESSAGE));
        } catch (error) {
            logResponseError(error as ErrorResponseObject);
            setFlags(addErrorFlag(flags, GET_FIELD_CONFIG_FAILED_MESSAGE));
        }
    };

    const setOptionsForLevel = (levelDepth: number, isAfterDataInitialLoad = false) => {
        if (levelDepth + 1 === currentNestingLevel && !isAfterDataInitialLoad) {
            return;
        }

        const updatedOptions = getOptionsWithoutEmptyOnesAndDuplicates(selectOptions);
        updateTreeWithCurrentState(
            options,
            currentParentOptionKey,
            currentNestingLevel,
            updatedOptions,
            setOptions,
            setIsSavingTriggered,
            !SAVE_TO_STORAGE
        );
        const levelOptions =
            levelDepth === 0 ?
                getAllOptionsByDepthLevel(options, levelDepth)
            :   getDepthLevelRelatedOptions(options, levelDepth, currentParentOptionKey);

        setSelectOptions(levelOptions);
        setLevelOptionsInitialState(copyArrayWithoutReference(levelOptions));
        setCurrentNestingLevel(levelDepth + 1);
        setNestingLevelChanged(true);
    };

    const onOptionsAdderChanged = (
        value: OptionsAdderAllowedValueTypes,
        updateType: OptionAdderOnChangeTypes,
        optionIndex: number,
        previouseOptionsWithUpdatedName: OptionWithUpdatedName[],
        optionKey = ''
    ) => {
        const addedOptionKey = updateOptionsAdder(value, updateType, selectOptions, setSelectOptions, optionIndex);

        if (updateType === TOGGLE_STATE_CHANGED) {
            updatePropertyInOption(options, IS_ENABLED, optionKey, value, setOptions);
        } else if (updateType === SORT_OPTIONS) {
            const updatedOptions = sortOptionsByGivenDirection(value as OptionsSortingData);

            currentNestingLevel > FIRST_NESTING_LEVEL ?
                updateTreeWithCurrentState(
                    options,
                    currentParentOptionKey,
                    currentNestingLevel,
                    updatedOptions,
                    setOptions,
                    setIsSavingTriggered,
                    !SAVE_TO_STORAGE
                )
            :   updateOrderInLevelOneOptions(updatedOptions, setOptions);
            selectOptions.length > 0 &&
                updateLevelsInfoFirstOptionKey(
                    selectOptions[0].key,
                    currentNestingLevel,
                    levelsInfo,
                    updatedOptions,
                    setLevelsInfo
                );
        } else if (updateType === REORDER_OPTIONS) {
            currentNestingLevel > FIRST_NESTING_LEVEL ?
                updateTreeWithCurrentState(
                    options,
                    currentParentOptionKey,
                    currentNestingLevel,
                    value as OptionItemObject[],
                    setOptions,
                    setIsSavingTriggered,
                    !SAVE_TO_STORAGE
                )
            :   updateOrderInLevelOneOptions(value as OptionItemObject[], setOptions);
            selectOptions.length > 0 &&
                updateLevelsInfoFirstOptionKey(
                    selectOptions[0].key,
                    currentNestingLevel,
                    levelsInfo,
                    value as OptionItemObject[],
                    setLevelsInfo
                );
        } else if (updateType === VALUE_CHANGED) {

            const updatedOptions = updateSingleOption(selectOptions, value, optionIndex, VALUE_UPDATE);

            const optionsWithUpdatedNameItems = getOptionsWithUpdatedName(
                levelOptionsInitialState,
                options,
                updatedOptions,
                currentParentOptionKey
            );
            const dedupOptionsWithUpdatedNameItems = uniqBy(
                [...previouseOptionsWithUpdatedName, ...optionsWithUpdatedNameItems],
                (item: OptionWithUpdatedName) => item.current.key
            );
            updateTreeWithCurrentState(
                options,
                currentParentOptionKey,
                currentNestingLevel,
                updatedOptions,
                setOptions,
                setIsSavingTriggered,
                !SAVE_TO_STORAGE
            );

            setIsOptionNameUpdated(dedupOptionsWithUpdatedNameItems.length > 0);
            setOptionsWithUpdatedName(dedupOptionsWithUpdatedNameItems);
        } else if (updateType === DEFAULT_VALUE_CHANGED) {
            const updatedOptions = updateDefaultOptionInCurrentLevel(selectOptions, optionIndex, value as boolean);
            updateTreeWithCurrentState(
                options,
                currentParentOptionKey,
                currentNestingLevel,
                updatedOptions,
                setOptions,
                setIsSavingTriggered,
                !SAVE_TO_STORAGE,
                STRAIGHT_UPDATE
            );
            setSelectOptions(updatedOptions);
        } else if (updateType === TRANSLATION_VALUE_CHANGED) {
            const updatedOptions = updateSingleOption(selectOptions, value, optionIndex, !VALUE_UPDATE, IS_TRANSLATION_UPDATE);
            updateTreeWithCurrentState(
                options,
                currentParentOptionKey,
                currentNestingLevel,
                updatedOptions,
                setOptions,
                setIsSavingTriggered,
                !SAVE_TO_STORAGE,
                STRAIGHT_UPDATE
            );
            setSelectOptions(updatedOptions);
        } else if (updateType === ADD_OPTION_CLICKED && addedOptionKey) {
            if (!selectOptions[0]?.key) {
                updateLevelInfoFirstOptionKey(
                    addedOptionKey,
                    levelsInfo,
                    currentNestingLevel,
                    currentParentOptionKey,
                    setLevelsInfo
                );
            }
        }
    };

    const isOptionAlreadyInTree = (optionToConsider: OptionItemObject): boolean => typeof optionToConsider !== UNDEFINED;

    const loadAddNewOptions = (): void => setSelectOptions([]);

    const loadChildrenForOption = (option: OptionItemObject): void => setSelectOptions(option.children);

    const childrenLengthChanged = (parent: OptionItemObject, currentViewOptions: OptionItemObject[]): boolean =>
        typeof parent !== UNDEFINED && parent.children?.length !== currentViewOptions.length;

    const parentHasNewChildren = (currentViewOptions: OptionItemObject[], parentNode: OptionItemObject): boolean =>
        childrenLengthChanged(parentNode, currentViewOptions);

    const optionHasChildren = (option: OptionItemObject) => option.children?.length > 0;

    const handleConfigureOptionsClicked = (key: string) => {
        const updatedOptions = getOptionsWithoutEmptyOnesAndDuplicates(selectOptions);

        if (options.length === 0) {
            setOptions([...updatedOptions]);
            setSelectOptions([]);
            setCurrentParentOptionKey(key);
            setCurrentNestingLevel(currentNestingLevel + 1);

            return;
        }

        const currentClickedOption = findOptionByPropertyAndValue(options, KEY, key);

        if (isOptionAlreadyInTree(currentClickedOption)) {
            const parentNode = getParentNode(options, currentClickedOption.depth!, currentClickedOption.key);

            if (isFirstBreadcrumbLevel(currentNestingLevel)) {
                setOptions(updateLevelOneOptions(options, updatedOptions));
            }

            if (parentHasNewChildren(updatedOptions, parentNode!)) {
                updateTreeOptionAndUpdateTreeDepths(options, parentNode!.key, updatedOptions, setOptions);
            }

            if (optionHasChildren(currentClickedOption)) {
                loadChildrenForOption(currentClickedOption);
                setLevelOptionsInitialState(copyArrayWithoutReference(currentClickedOption.children));
            } else {
                loadAddNewOptions();
            }
            setCurrentParentOptionKey(key);
        } else {
            if (isFirstBreadcrumbLevel(currentNestingLevel)) {
                setOptions(updateLevelOneOptions(options, updatedOptions));
                setCurrentParentOptionKey(key);
            } else {
                const levelChild = updatedOptions[0];
                const parentOption = getParentNode(options, levelChild.depth, levelChild.key);
                updateTreeOptionAndUpdateTreeDepths(
                    options,
                    parentOption ? parentOption.key : currentParentOptionKey,
                    updatedOptions,
                    setOptions
                );
                setCurrentParentOptionKey(key);
            }
            loadAddNewOptions();
        }
        setCurrentNestingLevel(currentNestingLevel + 1);
        setNestingLevelChanged(true);
    };

    const handleAddingBulkOptions = (newOptions: string[]) => {
        const firstOptionKey = handleBulkOptions(newOptions, selectOptions, currentNestingLevel, setSelectOptions);
        if (selectOptions.length === 0) {
            updateLevelInfoFirstOptionKey(
                firstOptionKey,
                levelsInfo,
                currentNestingLevel,
                currentParentOptionKey,
                setLevelsInfo
            );
        }
        setIsAddBulkOptionsModalOpened(false);
    };

    const handleDeleteOptionClick = (optionValue: string, optionKey: string, optionIndex: number) => {
        setOptionToDelete({ ...{ optionValue, optionKey, optionIndex } });
        setIsDeleteOptionModalOpened(true);
        setIsOptionDeletionInProgress(true);
    };

    const handleDeleteOptionModalClose = () => {
        setIsDeleteOptionModalOpened(false);
        setOptionToDelete({ ...OPTION_TO_DELETE_DEFAULTS });
    };

    const handleDeleteOptionSubmit = () => {
        const currentOption = findOptionByPropertyAndValue(options, KEY, optionToDelete.optionKey);
        const updatedOptions = removeOption(selectOptions, optionToDelete.optionIndex);

        if (currentOption) {
            const parentNode = getParentNode(options, currentOption.depth!, optionToDelete.optionKey);
            removeOptionFromTree(options, optionToDelete.optionKey, currentNestingLevel, setOptions);
            updateTreeWithCurrentState(
                options,
                parentNode ? parentNode.key : currentParentOptionKey,
                currentNestingLevel,
                updatedOptions,
                setOptions,
                setIsSavingTriggered,
                SAVE_TO_STORAGE,
                STRAIGHT_UPDATE
            );
        } else {
            setFlags(addSuccessFlag(flags, getSaveModelSuccessMessage(isOptionDeletionInProgress, isOptionNameUpdated)));
            setIsOptionDeletionInProgress(false);
        }
        setSelectOptions(updatedOptions);
        updatedOptions.length > 0 &&
            updateLevelsInfoFirstOptionKey(
                optionToDelete.optionKey,
                currentNestingLevel,
                levelsInfo,
                updatedOptions,
                setLevelsInfo
            );
    };

    const handleUpdateOptionNameModalClose = () => {
        setIsUpdateOptionNameModalOpened(false);
    };

    const handleUpdateOptionNameSubmit = async() => {
        const updatedOptions: OptionItemObject[] = getOptionsWithoutEmptyOnesAndDuplicates(selectOptions);
        updateTreeWithCurrentState(
            options,
            currentParentOptionKey,
            currentNestingLevel,
            updatedOptions,
            setOptions,
            setIsSavingTriggered,
            SAVE_TO_STORAGE
        );
        setSelectOptions(updatedOptions);
        await router.reload();
    };

    const handleSettingsSave = (isSavedSuccessfully: boolean): void => {
        setFlags(
            isSavedSuccessfully ?
                addSuccessFlag(flags, SETTINGS_SAVED_SUCCESS_MESSAGE)
            :   addErrorFlag(flags, SETTINGS_SAVED_FAILURE_MESSAGE)
        );
        setIsSettingsModalOpened(false);
    };

    const handleImport = (fileContent: ImportExportLevel): void => {
        setIsImportConfigModalOpened(false);
        const { mergedOptions, mergedLevelInfo } = mergeWithCurrentOptions(options, levelsInfo, fileContent);
        const optionsWithUpdatedName = getOptionsWithUpdatedNameForImport(
            levelOptionsInitialState,
            mergedOptions,
            [],
            undefined
        );
        setOptions(mergedOptions);
        setLevelsInfo(mergedLevelInfo);
        setSelectOptions(mergedOptions.map(item => ({ ...item, children: [] })));
        setOptionsForLevel(0, false);
        setFlags(addSuccessFlag(flags, IMPORT_SUCCESS_MESSAGE));
        setIsOptionNameUpdated(optionsWithUpdatedName.length > 0);
        setOptionsWithUpdatedName(optionsWithUpdatedName);
    };

    const handleResize = () => {
        document.body.style.overflowY = 'auto';
    };

    useEffect(() => {
        if (isSavingTriggered) {
            if (selectOptions.length > 0 || isUpdateLevelWithNoOptions(selectOptions)) {
                saveModel();
            }
        }
    }, [isSavingTriggered]);

    useEffect(() => {
        if (dataFetched && options.length > 0) {
            setOptionsForLevel(TOP_OF_THE_TREE, DATA_FOR_VIEW_LOADED);
        }
    }, [dataFetched]);

    useEffect(() => {
        const parentsData = getParentsDataForCurrentNestingLevel(options, currentParentOptionKey, currentNestingLevel);
        setDisabledParentOption(parentsData?.disabledParent);
        setCurrentLevelParents(parentsData?.parents);
    }, [nestingLevelChanged]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Wrapper>
            {isLoading ?
                <PositionedElement position={ABSOLUTE}>
                    <Spinner />
                </PositionedElement>
            :   <LicenseBanner isLicenseValid={isActive}>
                    <></>
                </LicenseBanner>
            }
            <ModalTransition>
                {isAddBulkOptionsModalOpened && (
                    <AddBulkOptionsModal
                        onSubmit={handleAddingBulkOptions}
                        onClose={() => setIsAddBulkOptionsModalOpened(false)}
                        currentOptions={selectOptions.map(option => option.value)}
                    />
                )}
            </ModalTransition>
            <ModalTransition>
                {isDeleteOptionModalOpened && optionToDelete.optionValue && (
                    <DeleteOptionModal
                        onSubmit={handleDeleteOptionSubmit}
                        onClose={handleDeleteOptionModalClose}
                        customField={editedFieldId}
                        optionValue={optionToDelete.optionValue}
                        optionKey={optionToDelete.optionKey}
                        indexOptionIsAt={currentNestingLevel - 1}
                        levelParents={currentLevelParents!}
                        setIsOptionDeletionInProgress={setIsOptionDeletionInProgress}
                    />
                )}
            </ModalTransition>
            <ModalTransition>
                {isUpdateOptionNameModalOpened && isOptionNameUpdated && (
                    <UpdateOptionNameModal
                        onSubmit={handleUpdateOptionNameSubmit}
                        onClose={handleUpdateOptionNameModalClose}
                        optionsWithUpdatedName={optionsWithUpdatedName}
                        options={options}
                        customField={editedFieldId}
                    />
                )}
            </ModalTransition>
            <ModalTransition>
                {isPreviewConfigurationModalOpened && (
                    <PreviewConfigurationModal
                        onClose={() => setIsPreviewConfigurationModalOpened(false)}
                        editedContextId={editedContextId}
                        previewData={options}
                        levelsInfo={levelsInfo}
                    />
                )}
            </ModalTransition>
            <ModalTransition>
                {isSettingsModalOpened && (
                    <SettingsModal
                        onClose={() => setIsSettingsModalOpened(false)}
                        handleSettingsSave={handleSettingsSave}
                        editedFieldId={editedFieldId}
                        editedContextId={editedContextId}
                    />
                )}
            </ModalTransition>
            <ModalTransition>
                {isImportConfigModalOpened && (
                    <ImportConfigurationModal
                        options={options}
                        onClose={() => setIsImportConfigModalOpened(false)}
                        onImportClick={handleImport}
                    />
                )}
            </ModalTransition>
            <TopBar
                onBreadcrumbClick={setOptionsForLevel}
                currentNestingLevel={currentNestingLevel}
                currentLevelParents={currentLevelParents}
                onFieldsRequiredClick={() => setIsSettingsModalOpened(true)}
                onImportConfigClick={() => setIsImportConfigModalOpened(true)}
            />
            <FieldConfigContent
                id={CONTENT}
                isLicenseValid={isActive}
            >
                {currentNestingLevel === MAX_NESTING_LEVEL && <LastLevelInfoMessage />}
                {disabledParentOption && <OptionDisabledWarning disabledOption={disabledParentOption} />}
                <LevelsInformation
                    levelsInfo={levelsInfo}
                    onTextChange={(keyToUpdate: typeof LEVEL_NAME_KEY | typeof LEVEL_DESCRIPTION_KEY, value: string) =>
                        updateLevelsInfoNameOrDescription(
                            keyToUpdate,
                            value,
                            levelsInfo,
                            currentNestingLevel,
                            selectOptions[0]?.key,
                            setLevelsInfo,
                            currentParentOptionKey
                        )
                    }
                    onTranslationChange={(
                        keyToUpdate: typeof LEVEL_NAME_TRANSLATION_KEY | typeof LEVEL_DESCRIPTION_TRANSLATION_KEY,
                        value: OptionTranslationValue
                    ) =>
                        updateLevelsInfoNameOrDescriptionTranslation(
                            keyToUpdate,
                            value,
                            levelsInfo,
                            currentNestingLevel,
                            selectOptions[0]?.key,
                            setLevelsInfo,
                            currentParentOptionKey
                        )
                    }
                    currentLevel={currentNestingLevel}
                    firstOptionKey={selectOptions.length > 0 ? selectOptions[0].key : ''}
                    parentOptionKey={currentParentOptionKey}
                    locale={locale}
                    nestingLevelChanged={nestingLevelChanged}
                />
                <OptionsAdder
                    onChange={onOptionsAdderChanged}
                    options={selectOptions}
                    optionsWithUpdatedName={optionsWithUpdatedName}
                    onConfigureOptionsClick={handleConfigureOptionsClicked}
                    currentNestingLevel={currentNestingLevel}
                    setIsAddBulkOptionsModalOpened={setIsAddBulkOptionsModalOpened}
                    setNestingLevelChanged={setNestingLevelChanged}
                    nestingLevelChanged={nestingLevelChanged}
                    handleDeleteOptionClick={handleDeleteOptionClick}
                    locale={locale}
                />
            </FieldConfigContent>
            <BottomSection>
                <FullWidthLine />
                <div className='buttons'>
                    <ButtonGroup>
                        <AtlaskitButton
                            onClick={() => {
                                setIsLoading(true);
                                void fetchData(CANCEL_TRIGGERED);
                            }}
                            testId='cancel-button'
                            isLoading={isSavingTriggered || isLoading}
                            appearance='subtle'
                        >
                            Discard
                        </AtlaskitButton>
                        <AtlaskitButton
                            onClick={() => setIsPreviewConfigurationModalOpened(true)}
                            isLoading={isSavingTriggered || isLoading}
                        >
                            Preview
                        </AtlaskitButton>
                        <AtlaskitButton
                            appearance='primary'
                            onClick={handleModelSave}
                            isLoading={isSavingTriggered || isLoading}
                        >
                            Save changes
                        </AtlaskitButton>
                    </ButtonGroup>
                </div>
            </BottomSection>

            <Flag
                flags={flags}
                setFlags={setFlags}
            />

        </Wrapper>
    );
};

export default CustomFieldConfig;
